import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '@/lib/api';

const TYPE_LABELS = {
  artist: 'Artist',
  curator: 'Curator',
  label: 'Label',
  other: 'Other',
};

const SOCIAL_LABELS = {
  website: 'Website',
  spotify: 'Spotify',
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  x: 'X',
  soundcloud: 'SoundCloud',
  bandcamp: 'Bandcamp',
  apple_music: 'Apple Music',
  beatport: 'Beatport',
  deezer: 'Deezer',
  tidal: 'TIDAL',
};

const toSafeExternalUrl = value => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }

  return `https://${raw}`;
};

export default function PublicProfile() {
  const { userId } = useParams();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError('');

      try {
        const { data } = await api.get(
          `/profiles/${encodeURIComponent(userId)}`
        );

        if (active) {
          setProfile(data);
        }
      } catch (e) {
        if (active) {
          setError(
            e.response?.data?.detail ||
            'Profile not found'
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [userId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-6">
        <div className="text-sm text-zinc-500">Loading profile…</div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-6">
        <div className="w-full max-w-lg border border-zinc-800 rounded-2xl bg-zinc-900/40 p-8 text-center">
          <div className="font-display text-2xl mb-2">Profile not found</div>
          <div className="text-sm text-zinc-500 mb-6">
            {error || 'This profile does not exist.'}
          </div>
          <Link
            to="/"
            className="inline-flex rounded-full border border-zinc-800 px-5 py-2 text-sm text-zinc-300 hover:text-white hover:border-zinc-600"
          >
            Back to TuneAmplifier
          </Link>
        </div>
      </div>
    );
  }

  const profileType =
    TYPE_LABELS[profile.profile_type] ||
    (profile.is_curator ? 'Curator' : 'TuneAmplifier member');

  const socialEntries = Object.entries(
    profile.social_links || {}
  ).filter(([, value]) => String(value || '').trim());

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <div className="max-w-[1100px] mx-auto p-6 lg:p-10">
        <div className="flex items-center justify-between gap-4 mb-6">
          <Link
            to="/"
            className="font-display tracking-tight text-lg"
          >
            TUNE<span className="text-[#ccff00]">AMPLIFIER</span>
          </Link>

          <Link
            to="/login"
            className="text-sm text-zinc-500 hover:text-zinc-200 transition-colors"
          >
            Sign in
          </Link>
        </div>

        <section className="border border-zinc-800 rounded-3xl bg-zinc-900/40 overflow-hidden">
          <div className="h-56 md:h-72 bg-zinc-950">
            {profile.banner_url ? (
              <img
                src={profile.banner_url}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950" />
            )}
          </div>

          <div className="p-6 md:p-8">
            <div className="flex items-end gap-5 flex-wrap">
              {profile.picture ? (
                <img
                  src={profile.picture}
                  alt=""
                  className="w-28 h-28 rounded-full object-cover border-4 border-zinc-950 bg-zinc-900 -mt-20"
                />
              ) : (
                <div className="w-28 h-28 rounded-full border-4 border-zinc-950 bg-zinc-800 -mt-20 flex items-center justify-center text-zinc-500">
                  TA
                </div>
              )}

              <div className="min-w-0 pb-1">
                <div className="font-display text-3xl md:text-4xl font-bold">
                  {profile.name}
                </div>
                <div className="text-xs text-zinc-500 font-mono uppercase tracking-wider mt-2">
                  {profileType}
                </div>

                {profile.email && (
                  <a
                    href={`mailto:${profile.email}`}
                    className="text-sm text-zinc-400 hover:text-white transition-colors mt-2 inline-block"
                  >
                    {profile.email}
                  </a>
                )}
              </div>
            </div>

            {profile.bio && (
              <div className="mt-7 max-w-3xl text-zinc-300 leading-relaxed whitespace-pre-wrap">
                {profile.bio}
              </div>
            )}

            {socialEntries.length > 0 && (
              <div className="mt-8 flex flex-wrap gap-2">
                {socialEntries.map(([key, value]) => {
                  const href = toSafeExternalUrl(value);
                  const label = SOCIAL_LABELS[key] || key;

                  return (
                    <a
                      key={key}
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-full border border-zinc-800 bg-zinc-950 px-4 py-2 text-sm text-zinc-300 hover:text-white hover:border-zinc-600 transition-colors"
                    >
                      {label}
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
